import React, { useEffect } from "react";
import { useAuth } from "src/context/AuthContext";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useProcessStepsStore } from "src/store/processStepsStore";
import { useTutorDashboardStore } from "src/store/tutorDashboardStore";
import { useTutorSessionsData } from "src/hooks/useTutorSessionsQuery";
import LoadingSpinner from "src/components/LoadingSpinner";
import NotificationDropdown from "src/components/notifications/NotificationDropdown";
import { EmptyState } from "src/components/ui/EmptyState";
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  FileText,
  Upload,
  Eye,
  MoreHorizontal,
  RefreshCcw,
  Video
} from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "src/components/ui/Avatar";
import { Button } from "src/components/ui/Button";
import { Card, CardContent, CardHeader, CardTitle } from "src/components/ui/Card";
import { Badge } from "src/components/ui/Badge";
import { format } from "date-fns";
import { usePageHeaderStore } from "src/store/pageHeaderStore";
import AvailabilityOverlapChart from "src/components/tutor/availability/AvailabilityOverlapChart";
import { ROUTES } from "src/routes/RouteConfig";
import { getSessionStatus, getSimpleSessionStatus } from "src/utils/sessionUtils";

// Timeline Navigator Component
const TimelineNavigator = ({
  selectedDate,
  setSelectedDate
}: {
  selectedDate: Date;
  setSelectedDate: (date: Date) => void
}) => {

  const handlePrevDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(selectedDate.getDate() - 1);
    setSelectedDate(newDate);
  };

  const handleNextDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(selectedDate.getDate() + 1);
    setSelectedDate(newDate);
  };

  return (
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center space-x-2">
        <button
          onClick={handlePrevDay}
          className="p-1 rounded-full hover:bg-gray-100"
        >
          <ChevronLeft size={20} />
        </button>
        <span className="text-sm font-medium">
          {format(selectedDate, "MMM d, yyyy")}
        </span>
        <button
          onClick={handleNextDay}
          className="p-1 rounded-full hover:bg-gray-100"
        >
          <ChevronRight size={20} />
        </button>
      </div>
      <div className="text-sm text-gray-500">
        {format(selectedDate, "EEEE, MMMM d, yyyy")}
      </div>
    </div>
  );
};

// Timeline Dots Component
const TimelineDots = ({ sessions }: { sessions: Array<any> }) => {
  if (!sessions || sessions.length === 0) {
    return (
      <div className="flex items-center justify-center mb-4 px-2">
        <div className="h-1 bg-gray-200 flex-grow"></div>
        <div className="text-sm text-gray-500 px-4">No sessions scheduled</div>
        <div className="h-1 bg-gray-200 flex-grow"></div>
      </div>
    );
  }

  return (
    <div className="flex items-center space-x-2 mb-4 px-2">
      <div className="h-1 bg-gray-200 flex-grow"></div>
      {sessions.map((session) => {
        const status = getSimpleSessionStatus(session);
        return (
          <div
            key={session.id}
            className={`h-3 w-3 rounded-full ${
              status === "completed" ? "bg-gray-400" :
              status === "current" ? "bg-green-500 animate-pulse" :
              "bg-blue-300"
            }`}
            title={`${session.topic?.name || 'Session'} - ${status}`}
          />
        );
      })}
      <div className="h-1 bg-gray-200 flex-grow"></div>
    </div>
  );
};

// Session Card Component
const SessionCard = ({ session, handleJoinSession }: { session: any; handleJoinSession: (session: any) => void }) => {
  const sessionTime = format(new Date(session.scheduled_at), "h:mm a");
  const studentName = session.student_profile 
    ? `${session.student_profile.first_name} ${session.student_profile.last_name}`
    : 'Unknown Student';
  
  const sessionStatus = getSessionStatus(session);

  return (
    <div className="mb-4 relative">
      <div className="flex items-start">
        <div className="w-20 text-sm text-gray-500 pt-1">{sessionTime}</div>
        <div className={`flex-1 p-4 rounded-lg border ${
          sessionStatus.status === "completed" || sessionStatus.status === "recently_ended" ? "bg-gray-50" :
          sessionStatus.status === "live" ? "bg-red-50 border-red-200" :
          "bg-white"
        }`}>
          <div className="font-medium">{session.topic?.name || 'Session'}</div>
          <div className="text-sm text-gray-600">{studentName} • {session.batch?.name || 'Grade'}</div>

          {/* Show Join Now button when session can be joined */}
          {sessionStatus.canJoin && (
            <div className="mt-2">
              <Button 
                size="sm" 
                onClick={() => handleJoinSession(session)}
                className={`${sessionStatus.status === 'live' ? 'bg-red-600 hover:bg-red-700 text-white animate-pulse' : 'bg-green-600 hover:bg-green-700'}`}
              >
                <Video className="h-4 w-4 mr-1" />
                {sessionStatus.status === 'live' ? 'Join Now' : 'Join'}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Quick Action Card Component
const QuickActionCard = ({
  icon,
  title,
  onClick
}: {
  icon: React.ReactNode;
  title: string;
  onClick: () => void
}) => {
  return (
    <Card
      className="flex flex-col items-center justify-center p-4 hover:bg-gray-50 cursor-pointer transition-colors"
      onClick={onClick}
    >
      <div className="p-3 rounded-lg bg-purple-100 text-purple-600 mb-2">
        {icon}
      </div>
      <div className="text-sm text-center">{title}</div>
    </Card>
  );
};

// Student Card Component
const StudentCard = ({ student }: { student: any }) => {
  return (
    <div className="flex items-center justify-between p-3 border-b last:border-b-0">
      <div className="flex items-center">
        <Avatar className="h-10 w-10">
          <AvatarImage src={student.photo} alt={student.name} />
          <AvatarFallback>{student.name.charAt(0)}</AvatarFallback>
        </Avatar>
        <div className="ml-3">
          <div className="font-medium">{student.name}</div>
          <div className="text-xs text-gray-500">{student.grade}</div>
        </div>
      </div>
      <div className="flex flex-col items-end">
        <div className="flex space-x-1 mb-1">
          {student.subjects.map((subject: string, index: number) => (
            <Badge key={index} variant="outline" className="text-xs bg-gray-50">
              {subject}
            </Badge>
          ))}
        </div>
        <button className="text-gray-400 hover:text-gray-600">
          <MoreHorizontal size={16} />
        </button>
      </div>
    </div>
  );
};


const TutorDashboard: React.FC = () => {
  const { isInitialized, loading } = useAuth();
  const location = useLocation();
  const processStepsStore = useProcessStepsStore.getState();
  const navigate = useNavigate();
  const { setTitle, setDescription, setActions, reset } = usePageHeaderStore();

  // Get state and actions from the dashboard store (UI state only)
  const {
    showWelcomeMessage,
    setShowWelcomeMessage,
    selectedDate,
    setSelectedDate
  } = useTutorDashboardStore();

  // Use React Query for session data with 10 minutes stale time
  const {
    todaySessions,
    isLoading: isLoadingSessions,
    hasError: hasSessionsError,
    error: sessionsError,
    refreshAll: refreshSessions
  } = useTutorSessionsData(selectedDate);

  // Join session handler - reused from UpcomingSessions.tsx
  const handleJoinSession = (session: any) => {
    // Navigate to join session page
    window.open(`/join-session/${session.id}`, '_blank');
  };

  // Sample students data (this would also come from React Query in a real implementation)
  const students = [
    {
      id: "st1",
      name: "Alex Johnson",
      grade: "Grade 11",
      subjects: ["Algebra", "Trigonometry"],
      photo: "/images/students/alex.jpg"
    },
    {
      id: "st2",
      name: "Sarah Williams",
      grade: "Grade 12",
      subjects: ["Organic Chemistry", "Reactions"],
      photo: "/images/students/sarah.jpg"
    },
    {
      id: "st3",
      name: "Michael Lee",
      grade: "Grade 11",
      subjects: ["Physics", "Mechanics"],
      photo: "/images/students/michael.jpg"
    }
  ];

  // Show loading state while auth is initializing
  if (loading || !isInitialized) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <LoadingSpinner />
          <p className="mt-4 text-gray-600">Fetching your details...</p>
        </div>
      </div>
    );
  }

  // Handle persisted process modal state
  useEffect(() => {
    console.log(
      "Dashboard: Checking for persisted process modal state",
      processStepsStore
    );

    // Check if we have a persisted process modal state
    if (processStepsStore.persistedState && processStepsStore.isOpen) {
      console.log("Dashboard: Found persisted process modal state");

      // Show success toast if coming from onboarding
      if (location.state?.onboardingComplete) {
        // Set the welcome message flag instead of using toast
        setShowWelcomeMessage(true);
      }

      // Clear the persisted state after a delay
      const timer = setTimeout(() => {
        console.log("Dashboard: Clearing persistence and closing modal");
        processStepsStore.preventStateReset(false); // Allow resets now
        processStepsStore.clearPersistence();
        processStepsStore.close();
      }, 5000);

      return () => clearTimeout(timer);
    } else {
      console.log("Dashboard: No persisted process modal state found");

      // Make sure resets are allowed when there's no persisted state
      processStepsStore.preventStateReset(false);
    }
  }, [location.state, processStepsStore, setShowWelcomeMessage]);

  // Handle welcome message
  useEffect(() => {
    if (location.state?.showWelcome) {
      setShowWelcomeMessage(true);

      // Clear the state after showing the message
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate, setShowWelcomeMessage]);

  // Handle quick action clicks
  const handleQuickAction = (action: string) => {
    switch (action) {
      case 'schedule':
        navigate(ROUTES.TUTOR_SCHEDULE.path);
        break;
      case 'notes':
        navigate('/tutor/notes');
        break;
      case 'feedback':
        navigate('/tutor/feedback');
        break;
      default:
        break;
    }
  };

  // Create a notification dropdown for the actions slot
  const notificationActions = <NotificationDropdown />;

  useEffect(() => {
    setTitle("Tutor Dashboard");
    setDescription("Overview of your teaching activity, upcoming sessions, and quick actions.");
    setActions(notificationActions);
    return () => reset();
  }, [setTitle, setDescription, setActions, reset]);

  return (
    <>
      {showWelcomeMessage && (
        <div className="bg-gradient-to-r from-purple-100 to-blue-100 rounded-lg shadow-md p-6 mb-6 border border-blue-200">
          <h2 className="text-xl font-semibold text-blue-800 mb-2">
            Welcome aboard!
          </h2>
          <p className="text-gray-700">
            Your tutor profile has been set up successfully. You're now ready
            to start your teaching journey!
          </p>
          <button
            onClick={() => setShowWelcomeMessage(false)}
            className="mt-3 text-sm text-blue-600 hover:text-blue-800"
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - Left and Center */}
        <div className="lg:col-span-2 space-y-6">
          {/* Today's Schedule */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Today's Schedule</CardTitle>
            </CardHeader>
            <CardContent>
              <TimelineNavigator
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
              />
              <TimelineDots sessions={todaySessions} />

              <div className="mt-6">
                {isLoadingSessions ? (
                  <div className="flex items-center justify-center py-8">
                    <LoadingSpinner />
                    <span className="ml-2">Loading sessions...</span>
                  </div>
                ) : hasSessionsError ? (
                  <div className="text-center py-6">
                    <p className="text-red-600 mb-4">{sessionsError}</p>
                    <Button onClick={refreshSessions} variant="outline">
                      <RefreshCcw className="h-4 w-4 mr-2" />
                      Try Again
                    </Button>
                  </div>
                ) : todaySessions && todaySessions.length > 0 ? (
                  todaySessions.map(session => (
                    <SessionCard key={session.id} session={session} handleJoinSession={handleJoinSession} />
                  ))
                ) : (
                  <EmptyState
                    title="No sessions today"
                    description="You don't have any sessions scheduled for today. Enjoy your free time!"
                    svgPath="M8 2v4l-3 3h18l-3-3V2H8zm0 0h12a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2V4a2 2 0 012-2z"
                    className="py-8"
                  />
                )}
              </div>
            </CardContent>
          </Card>

          {/* Availability Overlap Chart */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Tutor Availability with Timezone overlap</CardTitle>
            </CardHeader>
            <CardContent>
              {/* Always render the chart component - it handles its own loading and empty states */}
              <div className="p-0 -m-6">
                <AvailabilityOverlapChart key="availability-chart" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Sidebar */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                <QuickActionCard
                  icon={<Calendar size={20} />}
                  title="Mark Availability"
                  onClick={() => handleQuickAction('schedule')}
                />
                <QuickActionCard
                  icon={<FileText size={20} />}
                  title="New Curriculum"
                  onClick={() => handleQuickAction('curriculum')}
                />
                <QuickActionCard
                  icon={<Upload size={20} />}
                  title="Upload Notes"
                  onClick={() => handleQuickAction('notes')}
                />
                <QuickActionCard
                  icon={<Eye size={20} />}
                  title="View Feedback"
                  onClick={() => handleQuickAction('feedback')}
                />
              </div>
            </CardContent>
          </Card>

          {/* Student Roster */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg">Student Roster</CardTitle>
              <Link to="/tutor/students" className="text-sm text-purple-600 hover:text-purple-800 flex items-center">
                View All <ChevronRight size={16} />
              </Link>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y">
                {students.map(student => (
                  <StudentCard key={student.id} student={student} />
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default TutorDashboard;